import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useOutletContext } from 'react-router-dom'

import { PaymentProcessing } from '@/customer/components/billing/billing-primitives'
import { PaymentReconciliationFailed } from '@/customer/components/billing/payment-reconciliation-failed'
import { StripePaymentWidget } from '@/customer/components/billing/stripe-payment-widget'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import {
  petSubscriptionQueryKey,
  useGetPetSubscription,
} from '@/customer/hooks/use-get-pet-subscription'
import { invoicesQueryKey } from '@/customer/hooks/use-list-invoices'
import { useTimeout } from '@/customer/hooks/use-timeout'
import { captureEvent } from '@/customer/lib/posthog'

const SETTLEMENT_TIMEOUT_MS = 10_000

/**
 * Payment recovery page for incomplete subscriptions.
 *
 * Reached when a member returns to the portal after browsing away
 * from a failed checkout, or via warning banners on the account page.
 * Delegates payment UI to `StripePaymentWidget`; the
 * `PetSubscriptionGuard` wrapper in `app.tsx` polls for the status
 * change and redirects on success.
 */
export function PaymentFailedPage() {
  useDocumentTitle('Complete payment')
  const { pet, member } = useOutletContext<PetScopeContext>()
  const { data, isPending } = useGetPetSubscription(pet.id)
  const queryClient = useQueryClient()
  const reconciliation = useTimeout(SETTLEMENT_TIMEOUT_MS, () => {
    Sentry.captureException(
      new Error('Subscription not healthy after successful card confirmation'),
      { extra: { petId: pet.id } }
    )
  })

  function handleSuccess() {
    captureEvent('subscription_started', {
      patient_id: pet.id,
      plan: data?.subscription?.plan,
    })
    reconciliation.start()
    queryClient.invalidateQueries({ queryKey: petSubscriptionQueryKey(pet.id) })
    queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
  }

  const headline = [
    { text: 'Payment', color: 'blue' as const },
    { text: 'unsuccessful', color: 'destructive' as const },
  ]

  if (reconciliation.hasElapsed) {
    return (
      <PageContainer headline={headline}>
        <PaymentReconciliationFailed
          message={`We had some trouble setting up ${pet.name}'s subscription`}
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer headline={headline}>
      {isPending ? (
        <LoadingSpinner />
      ) : reconciliation.isPending ? (
        <PaymentProcessing />
      ) : (
        <StripePaymentWidget
          clientSecret={data?.incompletePayment?.clientSecret ?? null}
          hostedInvoiceUrl={data?.incompletePayment?.hostedInvoiceUrl ?? null}
          label="Complete payment"
          region={member.region}
          initialError={`We couldn't charge your card when setting up ${pet.name}'s membership. Please try again or choose a different card.`}
          onSuccess={handleSuccess}
        />
      )}
    </PageContainer>
  )
}
